import { Injectable, Injector, NgZone } from '@angular/core';
import { Messaging, getToken, isSupported, onMessage } from '@angular/fire/messaging';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env';
import { PushNotificationTokenService } from '@services/api/notification-api.service';

@Injectable({ providedIn: 'root' })
export class WebPushService {
  private syncing = false;
  private foregroundListenerAttached = false;

  constructor(
    private readonly injector: Injector,
    private readonly pushNotificationTokenService: PushNotificationTokenService,
    private readonly zone: NgZone,
  ) {}

  async syncIfPermitted(): Promise<void> {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }
    await this.registerCurrentToken();
  }

  async requestPermissionAndRegister(): Promise<'granted' | 'denied' | 'unsupported'> {
    if (!(await this.canUseWebPush())) {
      return 'unsupported';
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return permission === 'denied' ? 'denied' : 'unsupported';
    }
    await this.registerCurrentToken();
    return 'granted';
  }

  async canUseWebPush(): Promise<boolean> {
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
      return false;
    }
    if (!environment.firebaseVapidKey) {
      return false;
    }
    return isSupported();
  }

  get permission(): NotificationPermission | 'unsupported' {
    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }
    return Notification.permission;
  }

  private async registerCurrentToken(): Promise<void> {
    if (this.syncing) {
      return;
    }
    if (!(await this.canUseWebPush())) {
      return;
    }

    this.syncing = true;
    try {
      const registration = await navigator.serviceWorker.register(
        new URL('firebase-messaging-sw.js', document.baseURI).toString(),
        { scope: new URL('.', document.baseURI).pathname },
      );
      await navigator.serviceWorker.ready;

      const messaging = this.injector.get(Messaging);
      const token = await getToken(messaging, {
        vapidKey: environment.firebaseVapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) {
        return;
      }

      await firstValueFrom(this.pushNotificationTokenService.register({
        token,
        platform: 'web',
      }));

      if (!this.foregroundListenerAttached) {
        this.foregroundListenerAttached = true;
        onMessage(messaging, payload => {
          const title = payload.notification?.title ?? payload.data?.['title'] ?? 'Notification';
          const body = payload.notification?.body ?? payload.data?.['body'] ?? '';
          console.info('[web-push] foreground message', payload);
          this.zone.run(() => {
            void navigator.serviceWorker.ready.then(activeRegistration =>
              activeRegistration.showNotification(title, {
                body,
                data: payload.data,
                requireInteraction: true,
              }),
            );
          });
        });
      }
    } finally {
      this.syncing = false;
    }
  }
}
